import React from 'react';
import { motion } from 'framer-motion';
import { Settings as SettingsIcon, User, Bell, Shield, Database } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';

const Settings: React.FC = () => {
    return (
        <div className="space-y-6">
            {/* Header */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col space-y-2">
                <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
                <p className="text-gray-600">Manage your dashboard preferences and configuration</p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* User Preferences */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center space-x-2">
                                <User className="w-5 h-5" />
                                <span>User Preferences</span>
                            </CardTitle>
                            <CardDescription>Customize your dashboard experience</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Dark mode disabled - using only light mode */}
                            {/* <div className="flex items-center justify-between">
                                <Label htmlFor="dark-mode">Dark Mode</Label>
                                <Switch id="dark-mode" />
                            </div> */}

                            <div className="flex items-center justify-between">
                                <Label htmlFor="auto-refresh">Auto Refresh Dashboard</Label>
                                <Switch id="auto-refresh" defaultChecked />
                            </div>

                            <div className="flex items-center justify-between">
                                <Label htmlFor="compact-view">Compact View</Label>
                                <Switch id="compact-view" />
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Notifications */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center space-x-2">
                                <Bell className="w-5 h-5" />
                                <span>Notifications</span>
                            </CardTitle>
                            <CardDescription>Configure your notification preferences</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="email-alerts">Email Alerts</Label>
                                <Switch id="email-alerts" defaultChecked />
                            </div>

                            <div className="flex items-center justify-between">
                                <Label htmlFor="system-alerts">System Alerts</Label>
                                <Switch id="system-alerts" defaultChecked />
                            </div>

                            <div className="flex items-center justify-between">
                                <Label htmlFor="weekly-reports">Weekly Reports</Label>
                                <Switch id="weekly-reports" />
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Security */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center space-x-2">
                                <Shield className="w-5 h-5" />
                                <span>Security</span>
                            </CardTitle>
                            <CardDescription>Manage your security settings</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <Button variant="outline" className="w-full">
                                Change Password
                            </Button>

                            <Button variant="outline" className="w-full">
                                Two-Factor Authentication
                            </Button>

                            <Button variant="outline" className="w-full">
                                Active Sessions
                            </Button>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Data & Export */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center space-x-2">
                                <Database className="w-5 h-5" />
                                <span>Data & Export</span>
                            </CardTitle>
                            <CardDescription>Manage your data and export options</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <Button variant="outline" className="w-full">
                                Export Dashboard Data
                            </Button>

                            <Button variant="outline" className="w-full">
                                Download Reports
                            </Button>

                            <Button variant="outline" className="w-full">
                                Data Retention Settings
                            </Button>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>

            {/* System Information */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center space-x-2">
                            <SettingsIcon className="w-5 h-5" />
                            <span>System Information</span>
                        </CardTitle>
                        <CardDescription>Current system status and information</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                            <div className="space-y-2">
                                <div className="text-2xl font-bold text-blue-600">v2.1.0</div>
                                <div className="text-sm text-gray-600">Dashboard Version</div>
                            </div>

                            <div className="space-y-2">
                                <div className="text-2xl font-bold text-green-600">99.9%</div>
                                <div className="text-sm text-gray-600">System Uptime</div>
                            </div>

                            <div className="space-y-2">
                                <div className="text-2xl font-bold text-purple-600">24/7</div>
                                <div className="text-sm text-gray-600">Support Available</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    );
};

export default Settings;
